import Papa from "papaparse";
import * as XLSX from "xlsx";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ImportedEmail {
  raw: string;       // original value from file
  email: string;     // normalized (lowercase, trimmed)
  isValid: boolean;  // passes RFC-like regex test
}

// ── Internal helpers ───────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

function validateEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

/**
 * Given a 2D array of strings (rows × columns), detect the email column:
 * 1. First row is treated as headers. Look for a header matching
 *    "email", "e-mail", or "mail" (case-insensitive).
 * 2. If no header match, fall back to the first column whose values
 *    contain "@" for the majority of rows.
 *
 * Returns all raw email strings from the detected column (excluding the header row).
 */
export function extractEmailsFrom2DArray(rows: string[][]): string[] {
  if (rows.length < 2) return [];

  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const EMAIL_HEADERS = ["email", "e-mail", "mail"];

  let colIndex = headers.findIndex((h) => EMAIL_HEADERS.includes(h));

  if (colIndex < 0) {
    // Fallback: find first column where >50% of data rows contain "@"
    const dataRows = rows.slice(1);
    for (let c = 0; c < (rows[0]?.length ?? 0); c++) {
      const colValues = dataRows.map((r) => r[c] ?? "");
      const withAt = colValues.filter((v) => v.includes("@")).length;
      if (dataRows.length > 0 && withAt / dataRows.length > 0.5) {
        colIndex = c;
        break;
      }
    }
  }

  if (colIndex < 0) return [];

  return rows.slice(1).map((row) => row[colIndex] ?? "").filter(Boolean);
}

/**
 * Deduplicate, normalize, and validate a list of raw email strings.
 * Respects the 50-email cap (silently drops extras).
 */
export function processRawEmails(rawEmails: string[], cap = 50): ImportedEmail[] {
  const seen = new Set<string>();
  const result: ImportedEmail[] = [];

  for (const raw of rawEmails) {
    if (result.length >= cap) break;
    const email = normalizeEmail(raw);
    if (!email) continue;
    if (seen.has(email)) continue; // deduplicate
    seen.add(email);
    result.push({ raw, email, isValid: validateEmail(email) });
  }

  return result;
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Parse a CSV string and return ImportedEmail[].
 * Uses PapaParse — auto-detects delimiter.
 * Detects "email" / "e-mail" / "mail" header, or falls back to first "@" column.
 */
export function extractEmailsFromCSV(content: string): ImportedEmail[] {
  const result = Papa.parse<string[]>(content, {
    skipEmptyLines: true,
  });
  const rows: string[][] = result.data as string[][];
  const rawEmails = extractEmailsFrom2DArray(rows);
  return processRawEmails(rawEmails);
}

/**
 * Parse an XLSX/XLS ArrayBuffer and return ImportedEmail[].
 * Reads the first sheet only.
 * Same column-detection logic as extractEmailsFromCSV.
 */
export function extractEmailsFromXLSX(buffer: ArrayBuffer): ImportedEmail[] {
  const wb = XLSX.read(buffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows: string[][] = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1 });
  const rawEmails = extractEmailsFrom2DArray(rows);
  return processRawEmails(rawEmails);
}
